import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setPageOptions } from 'reducers/layout';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
import { setTableParams } from 'reducers/tableParams';
import { openModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import Panel from 'components/Panel';
import NothingBox from 'components/NothingBox';
import DateRangePicker from 'components/DatePicker/DateRangePicker';
import SearchBox from 'components/SearchBox';
import { CustomColumn } from 'components/DataTable/Columns';
import DataTable from 'components/DataTable/DataTable';
import { FeedOrderStatusBadge } from 'components/StatusBadge/StatusBadge';
import InfoTooltip from 'components/InfoTooltip';
import CreateFeedOrderModal from './components/modals/CreateFeedOrderModal';
import PendingFeedOrderCell from './components/PendingFeedOrderCell/PendingFeedOrderCell';
import PigGroupsExportModal from 'components/PigGroupsExportModal';
import download from 'downloadjs';
import moment from 'moment';
import { ReactComponent as DownloadIcon } from 'assets/svg/download-btn.svg';
// hooks
import useDataTable, { tableParamsSelector } from 'hooks/useDataTable';
import usePendingFeedOrders from 'hooks/usePendingFeedOrders';
// endpoints
import { getAllFeedOrders, downloadFeedOrdersCSV } from 'endpoints/feedOrders';
// utils, constants
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { isPendingFeedOrder } from 'utils/offlineFeedOrdersHelper';
import { convertLbsToKg } from 'utils/weightHelper';
import { formatNumber } from 'utils';
// styles
import './FeedOrdersList.scss';
import { tableNames } from '../../utils/constants';
// regional date handling
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser, formatDateTimeForUser, getMomentDisplayFormat } from 'utils/regionalDateHelper';

const FeedOrdersList = ({
  reqParams,
  router,
  setTableParams,
  setPageOptions,
  setBreadcrumbs,
  openModal,
  measurementSystem,
  countryCode,
  userId,
  companyId,
  isOnline,
  syncProgress,
  syncingData,
}) => {
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [farmSearch, setFarmSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const {
    data: {
      resources,
      isLoading,
      meta,
    },
    fetchData,
    onPageChange,
    onPerPageChange,
    onSortChange,
  } = useDataTable((p) => {
    const params = { ...p };

    // Only make API call when both dates are present or when no dates are present
    if (dateRange.startDate && dateRange.endDate) {
      params.startDate = dateRange.startDate.format('YYYY-MM-DD');
      params.endDate = dateRange.endDate.format('YYYY-MM-DD');
    } else if (dateRange.startDate || dateRange.endDate) {
      // If only one date is selected, don't make the API call
      return Promise.resolve({ resources: [], meta: { total: 0 } });
    }

    if (farmSearch) {
      params.farmSearch = farmSearch;
    }
    return getAllFeedOrders(params);
  }, {
    tableParams: reqParams,
    setTableParams: (params) => setTableParams(tableNames.feedOrdersAll, params),
  });

  const { page, per_page, sort } = reqParams;
  const total = parseInt(meta?.total || 0, 10);

  const exportFeedOrdersEnabled = !!meta?.export_feed_orders;

  const { pendingOrders, submittedOrders, removePendingOrder } = usePendingFeedOrders({
    userId,
    companyId,
    isOnline,
    syncProgress,
    syncingData,
    onSynced: () => fetchData(reqParams),
  });

  useEffect(() => {
    setPageOptions({ backLink: '' });
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.feedOrders" />,
        path: '/feed-orders-farms',
        useLabelAsMobileTitle: true,
      },
    ]);
  }, [setPageOptions, setBreadcrumbs]);

  useEffect(() => {
    // Only make API call when both dates are selected
    if (dateRange.startDate && dateRange.endDate) {
      const params = { ...reqParams, page: 1 };
      params.startDate = dateRange.startDate.format('YYYY-MM-DD');
      params.endDate = dateRange.endDate.format('YYYY-MM-DD');
      if (farmSearch) {
        params.farmSearch = farmSearch;
      } else {
        delete params.farmSearch;
      }
      fetchData(params).catch(toastResponseErrors);
    } else if (!dateRange.startDate && !dateRange.endDate) {
      // If no dates are selected, fetch without date filters
      const params = { ...reqParams, page: 1 };
      delete params.startDate;
      delete params.endDate;
      if (farmSearch) {
        params.farmSearch = farmSearch;
      } else {
        delete params.farmSearch;
      }
      fetchData(params).catch(toastResponseErrors);
    }
    // If only one date is selected, don't make API call
  }, [dateRange, farmSearch]);

  const onFarmClick = (farmId) => {
    router.push(`/feed-orders-farms/${farmId}`);
  };

  const handleOpenCreateModal = () => {
    openModal(<CreateFeedOrderModal />);
  };

  const handleExportCSV = async (modalParams = {}) => {
    try {
      setIsExporting(true);
      const apiParams = { ...reqParams };
      if (modalParams.date_start && modalParams.date_end) {
        apiParams.startDate = modalParams.date_start;
        apiParams.endDate = modalParams.date_end;
      } else if (dateRange.startDate && dateRange.endDate) {
        apiParams.startDate = dateRange.startDate.format('YYYY-MM-DD');
        apiParams.endDate = dateRange.endDate.format('YYYY-MM-DD');
      } else {
        delete apiParams.startDate;
        delete apiParams.endDate;
      }
      if (farmSearch) {
        apiParams.farmSearch = farmSearch;
      } else {
        delete apiParams.farmSearch;
      }
      const blob = await downloadFeedOrdersCSV(apiParams);
      const fileName = `EveryPig_Feed_Orders_${moment().format('MM-DD-YYYY-h-mm-a')}.csv`;
      download(blob, fileName, 'text/csv');
    } catch (e) {
      toastResponseErrors(e);
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenExportModal = () => {
    openModal(
      <PigGroupsExportModal
        onSubmit={handleExportCSV}
        title={<FormattedMessage id="general.exportCSV" />}
      />
    );
  };

  const renderFarmColumn = (data) => (
    <CustomColumn label={<FormattedMessage id="general.farm" />}>
      <button
        className="farm-link"
        onClick={() => onFarmClick(data.farm.id)}
        type="button"
      >
        {data.farm.name}
      </button>
    </CustomColumn>
  );

  const renderGroupColumn = (data) => (
    <CustomColumn label={<FormattedMessage id="general.group" />}>
      {data.pig_group.name}
    </CustomColumn>
  );

  const renderDeliveryDateColumn = (data) => (
    <CustomColumn label={<FormattedMessage id="general.deliveryDate" />}>
      {
        data.delivery_date ? formatDateForUser(data.delivery_date, countryCode) :
        <FormattedMessage id="general.notAvail" />
      }
    </CustomColumn>
  );

  const renderQuantityColumn = (data) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.orderQuantity" />}>
      {data.quantity ? (
        <span>
          {formatNumber(
            measurementSystem === 'metric'
              ? Math.round(convertLbsToKg(data.quantity))
              : Math.round(data.quantity)
          )}&nbsp;
          <FormattedMessage
            id={measurementSystem === 'metric' ? 'general.weight.kg' : 'general.weight.lbs'}
          />
        </span>
      ) : (
        <FormattedMessage id="general.notAvail" />
      )}
    </CustomColumn>
  );

  const renderBinNamesColumn = (data) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.binID" />}>
      {
        data.bin_names && data.bin_names.length ? data.bin_names.join(', ') :
        <FormattedMessage id="general.notAvail" />
      }
    </CustomColumn>
  );

  const renderStatusColumn = (data) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.status" />}>
      <FeedOrderStatusBadge status={data.status} />
    </CustomColumn>
  );

  const renderCreatedAtColumn = (data) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.created" />}>
      {isPendingFeedOrder(data) && (
        <PendingFeedOrderCell order={data} onDelete={removePendingOrder} showBadge={!exportFeedOrdersEnabled} />
      )}
      {!isPendingFeedOrder(data) && (
        data.created_at ? formatDateTimeForUser(data.created_at, countryCode) :
        <FormattedMessage id="general.notAvail" />
      )}
    </CustomColumn>
  );

  const renderNotesColumn = (data) => (
    <CustomColumn noBottomBorder label={<FormattedMessage id="general.comment" />}>
      {data.notes || <FormattedMessage id="general.notAvail" />}
    </CustomColumn>
  );

  const allColumns = [
    {
      label: <FormattedMessage id="component.dataTable.headers.created" />,
      flex: '1 1 140px',
      renderer: renderCreatedAtColumn,
      sortKey: 'created_at'
    },
    {
      label: <FormattedMessage id="general.farm" />,
      flex: '1 1 140px',
      renderer: renderFarmColumn,
      sortKey: 'farm_name'
    },
    {
      label: <FormattedMessage id="general.group" />,
      flex: '1 1 140px',
      renderer: renderGroupColumn,
      sortKey: 'group_name'
    },
    {
      label: <FormattedMessage id="component.dataTable.headers.binID" />,
      flex: '1 1 140px',
      renderer: renderBinNamesColumn,
      sortKey: 'bin_names'
    },
    {
      label: <FormattedMessage id="component.dataTable.headers.orderQuantity" />,
      flex: '1 1 140px',
      renderer: renderQuantityColumn,
      sortKey: 'quantity'
    },
    {
      label: <FormattedMessage id="general.deliveryDate" />,
      flex: '1 1 140px',
      renderer: renderDeliveryDateColumn,
      sortKey: 'delivery_date'
    },
    {
      label: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FormattedMessage id="component.dataTable.headers.status" />
          <InfoTooltip
            className="ml-5"
            overlay={<FormattedMessage id="container.FeedOrdersList.hint" />}
          />
        </div>
      ),
      flex: '1 1 140px',
      renderer: renderStatusColumn,
      sortKey: 'status'
    },
    {
      label: <FormattedMessage id="general.comment" />,
      flex: '1 1 140px',
      renderer: renderNotesColumn
    },
  ];

  const columns = exportFeedOrdersEnabled
    ? allColumns
    : allColumns.filter((col) => col.sortKey !== 'status');

  const paginationProps = {
    onPageChange,
    onPerPageChange,
    totalItems: parseInt(total, 10),
    currentPage: parseInt(page, 10),
    perPage: parseInt(per_page, 10),
  };

  const handleDateRangeChange = ({ startDate, endDate }) => {
    setDateRange({ startDate, endDate });
  };

  const handleClearDateFilter = () => {
    setDateRange({ startDate: null, endDate: null });
  };

  const handleFarmSearchChange = (searchValue) => {
    setFarmSearch(searchValue);
  };

  // Orders placed offline come first until the server receives them; offline, the last received order follows
  // unless the list fetched before the connection dropped already has it
  const submittedRows = submittedOrders.filter((order) => !resources.some((resource) => resource.id === order.id));
  const rows = Number(page) === 1 ? [...pendingOrders, ...submittedRows, ...resources] : resources;

  const filteredResources = farmSearch
    ? rows.filter((resource) => resource.farm.name.toLowerCase().includes(farmSearch.toLowerCase()))
    : rows;

  return (
    <section className="small-12 column feed-orders-list">
      <Panel>
        <Panel.Heading
          className="feed-orders-panel-title"
          title={(
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FormattedMessage id="general.feedOrders" />
            </div>
          )}
        >
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Add Feed Order Button */}
            <button
              type="button"
              className="button small primary"
              onClick={handleOpenCreateModal}
              style={{
                minWidth: '140px',
                whiteSpace: 'nowrap'
              }}
            >
              <i className="fa fa-plus" style={{ marginRight: '6px' }} />
              <FormattedMessage id="general.createFeedOrder" />
            </button>

            {/* Export CSV */}
            <div
              className="export-csv-btn"
              onClick={!isExporting ? handleOpenExportModal : undefined}
              role="button"
              tabIndex={0}
              onKeyPress={() => {}}
            >
              <DownloadIcon width={13} className="mh-5" />
              <FormattedMessage id="general.exportCSV" />
            </div>

            {/* Existing filters */}
            <div className="date-filter-container">
              <DateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onDatesChange={handleDateRangeChange}
                displayFormat={getMomentDisplayFormat(countryCode)}
                isOutsideRange={() => false}
              />
              {(dateRange.startDate || dateRange.endDate) && (
                <button
                  type="button"
                  className="button small secondary"
                  onClick={handleClearDateFilter}
                  style={{ marginLeft: '10px' }}
                >
                  <FormattedMessage id="general.clear" />
                </button>
              )}
            </div>
            <div style={{ minWidth: '200px' }}>
              <SearchBox
                placeholderKey="general.placeholder.searchByFarmName"
                onChange={handleFarmSearchChange}
                initialValue={farmSearch}
              />
            </div>
          </div>
        </Panel.Heading>
        <Panel.Body noPadding>
          <DataTable
            sort={sort}
            data={filteredResources}
            columns={columns}
            paginationProps={paginationProps}
            isLoading={isLoading}
            onSortChange={onSortChange}
            scrollable
          />

          <NothingBox display={!filteredResources.length} isLoading={isLoading}>
            <FormattedMessage tagName="h2" id="component.nothingBox.noData" />
          </NothingBox>
        </Panel.Body>
      </Panel>
    </section>
  );
};

FeedOrdersList.propTypes = {
  reqParams: T.object.isRequired,
  router: T.object.isRequired,
  setTableParams: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  setBreadcrumbs: T.func.isRequired,
  openModal: T.func.isRequired,
  measurementSystem: T.oneOf(['imperial', 'metric']),
  countryCode: T.string,
  userId: T.number,
  companyId: T.number,
  isOnline: T.bool.isRequired,
  syncProgress: T.number,
  syncingData: T.string,
};

export default connect(
  (state) => ({
    reqParams: tableParamsSelector(state, tableNames.feedOrdersAll),
    measurementSystem: state.auth.user.current_company.measurement_system,
    countryCode: getCurrentCountryCode(state),
    userId: state.auth.user.id,
    companyId: state.auth.user.current_company.id,
    isOnline: state.network.isOnline,
    syncProgress: state.network.progress,
    syncingData: state.network.syncingData,
  }),
  {
    setTableParams,
    setPageOptions,
    setBreadcrumbs,
    openModal,
  }
)(FeedOrdersList);
